/* eslint-disable no-underscore-dangle, import/no-unresolved, import/extensions */
import DOMPurify from 'isomorphic-dompurify';

import isNumber from 'lodash/isNumber';

import amountCheck from './amountCheck';
import state from './state';

export interface I18n {
  lang: string;
  locale: `${string}-${string}`;
  country: string;
  messages: Record<string, string>;
}

export const SUPPORTED_LANGUAGES = ['en', 'fr'] as const;
export const DEFAULT_LANGUAGE = 'en';

export type Lang = (typeof SUPPORTED_LANGUAGES)[number] | null;

const currentScriptSrc = globalThis.document?.currentScript?.src;

let moment = null;
let lang = null;
let urlLocale: string | null = null;

export function setLang(newLang) {
  lang = newLang;
  state.lang = lang;
  if (moment) {
    moment.locale(lang);
  }
}

export function setTranslations(language, translation) {
  state.translations[language] = translation;
  setLang(language);
}

export function getActiveLang() {
  if (!lang) {
    setLang(state.lang || null);
  }

  if (SUPPORTED_LANGUAGES.indexOf(lang) === -1) {
    setLang(DEFAULT_LANGUAGE);
  }

  return lang;
}

export function getActiveLocale() {
  const activeLang = getActiveLang();
  const activeCountry = getActiveCountry();

  return activeCountry ? `${activeLang}-${activeCountry}` : activeLang;
}

export function getUrlLocale() {
  return urlLocale ?? globalThis.location?.pathname.split('/')?.[1] ?? '';
}

export function setUrlLocale(urlLocaleValue: string) {
  urlLocale = urlLocaleValue;
}

export function getActiveCountry() {
  return state.country;
}

export function setActiveCountry(countryCode) {
  state.country = countryCode;
}

export function getAllTranslations(): Record<string, Record<string, string>> {
  return state.translations;
}

function getTranslatedStrings(newLang) {
  return getAllTranslations()[newLang];
}

function addVarsToString(string: string, vars: Record<string, string | number>) {
  let stringWithVars = string;
  Object.keys(vars).forEach((key) => {
    const v = String(vars[key]);
    const reg = new RegExp(`%${key}%`, 'g');

    stringWithVars = stringWithVars.replace(reg, DOMPurify.sanitize(v));
  });

  return stringWithVars;
}

function getCorrectPluralSentence(translation: string, count: number) {
  const translationsSplitted = translation.split(/\|(?![^{]*})/g);
  let returnPhrase = translation;

  if (translation.match(/[{}[\]].*/)) {
    translationsSplitted.some((p) => {
      const matches = p.match(/([{}[\]].*[{}[\]])(.*)/);

      if (matches[1] && amountCheck(matches[1], count)) {
        returnPhrase = matches[2].trim();
        return true;
      }
      returnPhrase = p;

      return false;
    });
  } else {
    return count <= 1 ? translationsSplitted[0].trim() : translationsSplitted[1].trim();
  }

  return returnPhrase;
}

export const getTranslate =
  (getCurrentLang = getActiveLang, getAllMessages = getTranslatedStrings) =>
  (key: string, vars?: Record<string, string | number>, count?: number) => {
    const currentLang = getCurrentLang();
    const allMessages = getAllMessages(currentLang);

    const translatedText = allMessages?.[key];
    let returnedTranslation = translatedText ?? key;

    const isTranschoice = isNumber(count) && returnedTranslation.match(/\|/);
    if (isTranschoice) {
      returnedTranslation = getCorrectPluralSentence(returnedTranslation, count);
    }

    if (typeof vars !== 'undefined') {
      returnedTranslation = addVarsToString(returnedTranslation, vars);
    }

    if (typeof window !== 'undefined' && window.__TRANSLATION_DEVTOOLS__) {
      window.__TRANSLATION_DEVTOOLS__.addKey(key, translatedText || '');
    }

    return returnedTranslation;
  };

const translate = getTranslate();

export { translate };

export function reportAllTranslationsToExtension(language: string) {
  if (typeof window !== 'undefined' && window.__TRANSLATION_DEVTOOLS__ && currentScriptSrc) {
    const basePath = currentScriptSrc.substring(0, currentScriptSrc.lastIndexOf('/') + 1);

    const callExtension = (info?: {
      mapping: {
        original: string;
        translations: Record<string, string>;
      }[];
      currentLang: string;
    }) => {
      window?.__TRANSLATION_DEVTOOLS__.addAllTranslations(getTranslatedStrings(language), info);
    };

    fetch(`${basePath}/langs/translation-mapping.json`)
      .then((response) => response.json())
      .then((mapping) => {
        callExtension({
          mapping,
          currentLang: language,
        });
      })
      .catch(() => {
        callExtension();
      });
  }
}

export function setMoment(momentLib) {
  moment = momentLib;
}

export const onReady = () => {
  reportAllTranslationsToExtension(getActiveLang());
};

export function createI18nContext(i18n: I18n) {
  const context = {
    i18n,
    translate: getTranslate(
      () => i18n.lang,
      () => i18n.messages
    ),
  };

  return context;
}
